/**
 * SidebarStep6 - Final Report Generation (Historical BI Path)
 * 
 * This component handles report generation for the Historical BI path.
 * Shows when calculation_mode === 'standard_bi' or default
 * 
 * Features:
 *   - Report generation with status
 *   - BI Calculation Detail display
 *   - Payroll Reconciliation results
 *   - Sensitivity Analysis
 *   - Monte Carlo Analysis
 *   - Data Entry Briefing download
 *   - Report download options (Standard, AI Narrative, Bundle)
 * 
 * Preserves ALL functionality from Step6.tsx
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/TestAuthContext';
import axios from '@/lib/http';
import WizardV2Layout from './WizardV2Layout';
import type { QAItem } from './WizardSidebar';
import NarrativeEditor from '../NarrativeEditor';
import { FinancialValue } from '@/components/shared/FinancialValue';
import { formatCurrency } from '@/utils/financialFormat';
import { useActivityLog } from '../../../hooks/useActivityLog';
import { apiFetch } from '../../../utils/api';
import { resolveCoverageYear } from '../../../utils/resolveCoverageYear';
import { 
  FileText, 
  Download, 
  CheckCircle, 
  AlertTriangle,
  Loader2,
  BarChart3,
  TrendingUp,
  DollarSign,
  Calculator,
  FileSpreadsheet,
  Sparkles,
  Package,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Info
} from 'lucide-react';

interface SidebarStep6Props {
  goToStep?: (step: number) => void;
  onBack: () => void;
  onComplete: () => void;
  data: any;
  onSave?: (explicitData?: any) => Promise<void>;
  isSaving?: boolean;
  lastSaved?: Date | null;
  highestCompletedStep?: number;
  backendCompletionPercent?: number;
  claimId?: string;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SidebarStep6({
  onBack,
  onComplete,
  goToStep,
  data,
  onSave,
  isSaving,
  lastSaved,
  highestCompletedStep,
  backendCompletionPercent,
  claimId
}: SidebarStep6Props) {
  const [status, setStatus] = useState<'generating' | 'success' | 'error'>('generating');
  const [reportId, setReportId] = useState<string | null>(null);
  const [calculationResults, setCalculationResults] = useState<any>(null);
  const [showNarrator, setShowNarrator] = useState(false);
  const [error, setError] = useState<string>('');
  
  // Sensitivity Analysis State
  const [showSensitivity, setShowSensitivity] = useState(false);
  const [sensitivityParam, setSensitivityParam] = useState('payroll');
  const [sensitivityResults, setSensitivityResults] = useState<any>(null);
  const [sensitivityLoading, setSensitivityLoading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  
  // Monte Carlo Analysis State
  const [showMonteCarlo, setShowMonteCarlo] = useState(false);
  const [monteCarloResults, setMonteCarloResults] = useState<any>(null);
  const [monteCarloLoading, setMonteCarloLoading] = useState(false);
  
  // Data Entry Briefing State
  const [briefingLoading, setBriefingLoading] = useState(false);
  
  const { getToken } = useAuth();
  const { logActivity } = useActivityLog();
  const navigate = useNavigate();
  const hasGenerated = useRef(false);

  // Handle authenticated PDF download
  const handleDownloadPdf = async () => {
    if (!reportId) return;
    
    try {
      const token = await getToken();
      const blob = await apiFetch<Blob>(`/api/v1/download/report-pdf/${reportId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        showErrorToast: false
      });
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `BI_Report_${reportId.substring(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      logActivity({
        reportId,
        actionType: 'download',
        actionDetails: 'Downloaded Standard Report PDF',
        section: 'step6',
        metadata: { download_type: 'standard_pdf' }
      });
    } catch (error) {
      console.error('PDF download failed:', error);
      setValidationError(`Download failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  useEffect(() => {
    if (!hasGenerated.current) {
      hasGenerated.current = true;
      // If we already have a report_id from a previous calculation (e.g., remount),
      // restore results instead of regenerating
      if (data.generated_report_id && data.last_calculation_results) {
        console.log('♻️ Restoring previous calculation results, skipping re-generation');
        setReportId(data.generated_report_id);
        setCalculationResults(data.last_calculation_results);
        setStatus('success');
      } else {
        generateReport();
      }
    }
  }, []);

  const generateReport = async () => {
    try {
      const token = await getToken();
      
      const years = data.calculated_years || [];
      const coverageYear = years.length > 0
        ? years[years.length - 1]
        : resolveCoverageYear(data);
      if (coverageYear === null || coverageYear === undefined) {
        console.error('❌ SidebarStep6: cannot generate report — no coverage year in claim data');
        alert('Cannot generate report: this claim has no coverage year (no event date, no financial data years, no tax data). Please return to the setup step and set the loss event date.');
        return;
      }
      
      const payrollData: {[key: string]: number} = {};
      const employeePayrollData: {[key: string]: number} = {};
      const partnerK1Data: {[key: string]: number} = {};
      
      years.forEach((year: number) => {
        if (data[`payroll_${year}`]) {
          payrollData[`payroll_${year}`] = parseFloat(data[`payroll_${year}`]) || 0;
        }
        if (data[`employee_payroll_${year}`]) {
          employeePayrollData[`employee_payroll_${year}`] = parseFloat(data[`employee_payroll_${year}`]) || 0;
        }
        if (data[`partner_k1_income_${year}`]) {
          partnerK1Data[`partner_k1_income_${year}`] = parseFloat(data[`partner_k1_income_${year}`]) || 0;
        }
      });
      
      const response = await axios.post(
        `${BACKEND_URL}/api/v1/reports/generate`,
        {
          client_name: data.client_name,
          claimant_name: data.claimant_name,
          business_address: data.business_address,
          zone: data.zone,
          business_type: data.business_type,
          financial_data: data.financial_data || {},
          plFiles: data.plFiles || [],
          tax1065Files: data.tax1065Files || [],
          uploadedDocuments: data.uploadedDocuments || [],
          tax_data: data.tax_data || [],
          reconciliation: data.reconciliation || [],
          event_type: data.event_type,
          event_start_date: data.event_start_date,
          event_end_date: data.event_end_date,
          calculation_mode: data.calculation_mode || 'standard_bi',
          threshold: parseFloat(data.threshold) || 0,
          threshold_type: data.threshold_type || 'dollar',
          accountant_fees: parseFloat(data.accountant_fees) || 0,
          continuing_expenses: parseFloat(data.continuing_expenses) || 0,
          extra_expenses: parseFloat(data.extra_expenses) || 0,
          adjustment_notes: data.adjustment_notes || '',
          payroll_data: data.payroll_data || payrollData,
          employee_payroll_data: employeePayrollData,
          partner_k1_data: data.partner_k1_data || partnerK1Data,
          calculated_years: years,
          coverage_year: coverageYear,
          ...payrollData,
          ...employeePayrollData,
          ...partnerK1Data,
          // SSoT backlink: without this, every wizard Step-6 auto-fire creates
          // an orphaned report doc (draft_id=None) in claimsync.reports.
          // Backend uses this at reports_mongo.py:1214 to set drafts.report_id.
          draft_id: claimId,
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setReportId(response.data.report_id);
      setCalculationResults(response.data.calculation_results || {});
      
      logActivity({
        reportId: response.data.report_id,
        actionType: 'generate',
        // A canonical draft has no flat data.client_name, so this interpolated
        // the literal string "undefined" into the activity log.
        actionDetails: data.client_name
          ? `Generated BI report for ${data.client_name}`
          : 'Generated BI report',
        section: 'wizard',
        metadata: { 
          calculation_mode: data.calculation_mode || 'standard_bi',
          claim_amount: response.data.calculation_results?.final_claim_amount 
        }
      });
      
      let finalResults = response.data.calculation_results || {};
      if (response.data.payroll_reconciliation) {
        finalResults = {
          ...finalResults,
          payroll_reconciliation: response.data.payroll_reconciliation
        };
      }
      setCalculationResults(finalResults);

      // Persist to parent data so remount doesn't re-generate
      if (onSave) {
        try {
          await onSave({
            ...data,
            generated_report_id: response.data.report_id,
            last_calculation_results: finalResults,
          });
        } catch (saveErr) {
          console.warn('Failed to persist calculation results:', saveErr);
        }
      }

      setStatus('success');
    } catch (err: any) {
      console.error('Report generation failed:', err);
      setError(err.response?.data?.detail || 'Failed to generate report');
      setStatus('error');
    }
  };

  // Download Data Entry Briefing Report as PDF
  const downloadBriefing = async () => {
    if (!reportId) {
      setValidationError('Report must be generated first');
      return;
    }
    
    setBriefingLoading(true);
    try {
      const token = await getToken();
      
      const blob = await apiFetch<Blob>(
        `/api/v1/reports/briefing/${reportId}?format=pdf`,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/pdf'
          },
          showErrorToast: false
        }
      );
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Briefing_Report_${reportId.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading briefing:', err);
      setValidationError('Failed to download briefing. Please try again.');
    } finally {
      setBriefingLoading(false);
    }
  };

  // Run Sensitivity Analysis
  const runSensitivityAnalysis = async () => {
    if (!reportId) {
      setValidationError('Report must be generated first');
      return;
    }
    
    setSensitivityLoading(true);
    setSensitivityResults(null);
    
    try {
      const token = await getToken();
      
      const result = await new Promise<any>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BACKEND_URL}/api/v1/reports/${reportId}/sensitivity?parameter=${sensitivityParam}&min_pct=80&max_pct=120&step_pct=5`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              reject(new Error('Failed to parse response'));
            }
          } else {
            try {
              const errorData = JSON.parse(xhr.responseText);
              if (errorData.detail?.error === "CAUSATION_TEST_FAILED") {
                reject(new Error(`${errorData.detail.message}\n\n${errorData.detail.fix}`));
              } else {
                reject(new Error(errorData.detail || `HTTP ${xhr.status}: ${xhr.statusText}`));
              }
            } catch (e) {
              reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
            }
          }
        };
        
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send();
      });
      
      setSensitivityResults(result);
    } catch (err: any) {
      console.error('Sensitivity analysis error:', err);
      setValidationError(`Failed to run sensitivity analysis: ${err.message}`);
    } finally {
      setSensitivityLoading(false);
    }
  };

  // Run Monte Carlo Analysis
  const runMonteCarloAnalysis = async () => {
    if (!reportId) {
      setValidationError('Report must be generated first');
      return;
    }
    
    setMonteCarloLoading(true);
    setMonteCarloResults(null);
    
    try {
      const token = await getToken();
      
      const result = await new Promise<any>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BACKEND_URL}/api/v1/reports/${reportId}/monte-carlo?iterations=1000&noise_pct=0.15&seed=42`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              reject(new Error('Failed to parse response'));
            }
          } else {
            try {
              const errorData = JSON.parse(xhr.responseText);
              if (errorData.detail?.error === "CAUSATION_TEST_FAILED") {
                reject(new Error(`${errorData.detail.message}\n\n${errorData.detail.fix}`));
              } else {
                reject(new Error(errorData.detail || `HTTP ${xhr.status}: ${xhr.statusText}`));
              }
            } catch (e) {
              reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
            }
          }
        };
        
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send();
      });
      
      setMonteCarloResults(result);
    } catch (err: any) {
      console.error('Monte Carlo error:', err);
      setValidationError(`Failed to run Monte Carlo analysis: ${err.message}`);
    } finally {
      setMonteCarloLoading(false);
    }
  };

  // Build sidebar sections
  const buildSections = () => {
    const sections = [
      {
        id: 'status',
        label: 'Report Status',
        isActive: true,
        isComplete: status === 'success',
        onClick: () => document.getElementById('status-section')?.scrollIntoView({ behavior: 'smooth' })
      }
    ];
    
    if (status === 'success') {
      sections.push(
        {
          id: 'calculation',
          label: 'BI Calculation',
          isActive: false,
          isComplete: true,
          onClick: () => document.getElementById('calculation-section')?.scrollIntoView({ behavior: 'smooth' })
        },
        {
          id: 'payroll',
          label: 'Payroll Reconciliation',
          isActive: false,
          isComplete: !!calculationResults?.payroll_reconciliation,
          onClick: () => document.getElementById('payroll-section')?.scrollIntoView({ behavior: 'smooth' })
        },
        {
          id: 'sensitivity',
          label: 'Sensitivity Analysis',
          isActive: false,
          isComplete: !!sensitivityResults,
          onClick: () => document.getElementById('sensitivity-section')?.scrollIntoView({ behavior: 'smooth' })
        },
        {
          id: 'montecarlo',
          label: 'Monte Carlo',
          isActive: false,
          isComplete: !!monteCarloResults,
          onClick: () => document.getElementById('montecarlo-section')?.scrollIntoView({ behavior: 'smooth' })
        },
        {
          id: 'downloads',
          label: 'Download Reports',
          isActive: false,
          isComplete: false,
          onClick: () => document.getElementById('downloads-section')?.scrollIntoView({ behavior: 'smooth' })
        }
      );
    }
    
    return sections;
  };

  // Build QA items
  const buildQAItems = (): QAItem[] => {
    const items: QAItem[] = [];

    if (status === 'generating') {
      items.push({ id: 'generating', label: 'Generating report...', status: 'warning' });
    } else if (status === 'success') {
      items.push({ id: 'generated', label: 'Report generated', status: 'complete' });
      if (calculationResults?.final_claim_amount) {
        items.push({ id: 'claim', label: `Claim: ${formatCurrency(calculationResults.final_claim_amount, { cents: true })}`, status: 'complete' });
      }
    } else if (status === 'error') {
      items.push({ id: 'error', label: 'Generation failed', status: 'missing' });
    }

    return items;
  };

  // Completion percent and letter grade are NOT derived from local UI state.
  // Backend is the SSoT. The previous mapping (status -> 100/90/50 and
  // 'A'/'B'/'D') was an invented frontend value — see
  // docs/plans/2026-04-25-frontend-calc-drift-audit.md (Fix B).

  // Show Narrator Editor
  if (showNarrator) {
    return (
      <NarrativeEditor
        claimId={reportId || 'demo'}
        claimData={data}
        calculationResults={calculationResults || {}}
        eventType={data.event_type || 'Hurricane'}
      />
    );
  }

  // Handle Quick Actions from sidebar
  const handleQuickAction = (action: string) => {
    console.log('⚡ Quick Action triggered (Step 6):', action);
    switch (action) {
      case 'rerun':
        // Re-run the analysis - go back to Step 5
        if (goToStep) {
          goToStep(5);
        }
        break;
      case 'preview':
        // Scroll to report preview section
        const reportSection = document.getElementById('report-section');
        if (reportSection) {
          reportSection.scrollIntoView({ behavior: 'smooth' });
        }
        break;
      default:
        console.log('Unknown quick action:', action);
    }
  };

  return (
    <WizardV2Layout
      currentStep={6}
      highestCompletedStep={highestCompletedStep}
      completionPercent={backendCompletionPercent}
      stepTitle="Final Report"
      stepSubtitle="Historical BI Claim Report"
      calculationMode={data.calculation_mode || 'standard_bi'}
      sections={buildSections()}
      qaItems={buildQAItems()}
      isSaving={isSaving}
      lastSaved={lastSaved}
      onSave={onSave}
      goToStep={goToStep}
      onBack={onBack}
      showNext={false}
      claimId={claimId}
      onQuickAction={handleQuickAction}
    >
      <div className="p-6 lg:p-8 space-y-6">

        {validationError && (
          <p className="cs-meta" style={{ color: 'var(--color-needs-human-text)' }}>{validationError}</p>
        )}

        {/* Generating Status */}
        {status === 'generating' && (
          <div id="status-section" className="rounded-xl p-12 text-center" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
            <Loader2 className="w-16 h-16 animate-spin mx-auto mb-4" style={{ color: 'var(--color-brand)' }} />
            <h2 className="cs-display mb-2">Generating your BI report...</h2>
            <p className="cs-meta">This may take a few moments</p>
          </div>
        )}

        {/* Error Status */}
        {status === 'error' && (
          <div id="status-section" className="rounded-xl p-12 text-center" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'var(--color-needs-human-tint)' }}>
              <AlertTriangle className="w-8 h-8" style={{ color: 'var(--color-brand)' }} />
            </div>
            <h2 className="cs-display mb-2" style={{ color: 'var(--color-needs-human-text)' }}>Generation Failed</h2>
            <p className="cs-body mb-6">{error}</p>

            <div className="flex gap-4 justify-center">
              <button
                onClick={onBack}
                className="px-6 py-3 rounded-xl transition-colors"
                style={{ color: 'var(--color-text-secondary)', background: 'var(--color-bg-elevated)', fontWeight: 600 }}
              >
                Back
              </button>
              <button
                onClick={() => {
                  hasGenerated.current = false;
                  setStatus('generating');
                  generateReport();
                }}
                className="px-8 py-3 rounded-xl hover:shadow-lg transition-all"
                style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 600 }}
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Success Status */}
        {status === 'success' && (
          <>
            {/* Success Banner */}
            <div id="status-section" className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-verified)' }}>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'transparent' }}>
                  <CheckCircle className="w-6 h-6" style={{ color: 'var(--color-verified)' }} />
                </div>
                <div>
                  <h2 className="cs-h2" style={{ color: 'var(--color-verified)' }}>Report Generated Successfully!</h2>
                  <p className="cs-meta">Report ID: {reportId}</p>
                </div>
              </div>
            </div>

            {/* Final Claim Amount */}
            {calculationResults?.final_claim_amount && (
              <div className="rounded-xl p-8 text-center" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
                <p className="cs-eyebrow mb-2">FINAL CLAIM AMOUNT</p>
                <p className="cs-figure-lg" style={{ fontSize: '3rem', color: 'var(--color-needs-human-text)' }}>
                  <FinancialValue value={calculationResults.final_claim_amount} cents />
                </p>
              </div>
            )}

            {/* BI Calculation Detail */}
            {calculationResults?.calculation_detail && (
              <div id="calculation-section" className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-needs-human-tint)' }}>
                    <Calculator className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                  </div>
                  <h3 className="cs-h3">BI Calculation Detail</h3>
                </div>
                
                {(() => {
                  const detail = calculationResults.calculation_detail;
                  const bp = detail.benchmark_period;
                  const cp = detail.coverage_period;
                  const vp = detail.variable_profit_analysis;
                  const ga = detail.growth_adjustment;
                  const ae = detail.additional_expenses;
                  const fc = detail.final_calculation;
                  
                  return (
                    <div className="space-y-4">
                      {/* Benchmark Period */}
                      <div className="rounded-lg p-4" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
                        <h4 className="mb-2" style={{ color: 'var(--color-text-primary)', fontWeight: 700 }}>Benchmark Period Selection</h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="cs-meta">Period:</p>
                            <p className="cs-figure" style={{ color: 'var(--color-text-primary)', fontWeight: 700 }}>{bp.start} - {bp.end}</p>
                          </div>
                          <div>
                            <p className="cs-meta">Type:</p>
                            <p style={{ color: 'var(--color-text-primary)', fontWeight: 700 }}>{bp.type}</p>
                          </div>
                          <div>
                            <p className="cs-meta">Benchmark Revenue:</p>
                            <p className="cs-figure" style={{ color: 'var(--color-text-primary)', fontWeight: 700 }}><FinancialValue value={bp.revenue} /></p>
                          </div>
                          <div>
                            <p className="cs-meta">Benchmark VP:</p>
                            <p className="cs-figure" style={{ color: 'var(--color-text-primary)', fontWeight: 700 }}><FinancialValue value={bp.variable_profit} /></p>
                          </div>
                        </div>
                        {bp.why_selected && (
                          <div className="mt-3 p-2 rounded cs-meta" style={{ background: 'var(--color-bg-hover)' }}>
                            <strong>Why selected:</strong> {bp.why_selected}
                          </div>
                        )}
                      </div>

                      {/* Coverage Period */}
                      <div className="rounded-lg p-4" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
                        <h4 className="mb-2" style={{ color: 'var(--color-needs-human-text)', fontWeight: 700 }}>Coverage Period (Event Year)</h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="cs-meta">Period:</p>
                            <p className="cs-figure" style={{ color: 'var(--color-needs-human-text)', fontWeight: 700 }}>{cp.start} - {cp.end}</p>
                          </div>
                          <div>
                            <p className="cs-meta">Coverage VP:</p>
                            <p className="cs-figure" style={{ color: 'var(--color-needs-human-text)', fontWeight: 700 }}><FinancialValue value={cp.variable_profit} /></p>
                          </div>
                        </div>
                      </div>

                      {/* Variable Profit Loss */}
                      <div className="rounded-lg p-4" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
                        <h4 className="mb-2" style={{ color: 'var(--color-text-primary)', fontWeight: 700 }}>Step 1: Variable Profit Loss</h4>
                        <div className="cs-figure space-y-1">
                          <div className="flex justify-between">
                            <span>Benchmark VP:</span>
                            <span style={{ fontWeight: 700 }}><FinancialValue value={vp.benchmark_vp} /></span>
                          </div>
                          <div className="flex justify-between" style={{ color: 'var(--color-text-muted)' }}>
                            <span>Coverage VP:</span>
                            <span style={{ fontWeight: 700 }}><FinancialValue value={-vp.coverage_vp} /></span>
                          </div>
                          <div className="pt-1 flex justify-between" style={{ borderTop: '1px solid var(--color-border)', color: 'var(--color-text-primary)', fontWeight: 700 }}>
                            <span>VP Loss:</span>
                            <span><FinancialValue value={vp.vp_loss} /></span>
                          </div>
                        </div>
                      </div>

                      {/* Growth Adjustment */}
                      <div className="rounded-lg p-4" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
                        <h4 className="mb-2" style={{ color: 'var(--color-text-primary)', fontWeight: 700 }}>Step 2: Growth/Trend Adjustment</h4>
                        <div className="cs-figure space-y-1">
                          <div className="flex justify-between">
                            <span>Claimant Factor:</span>
                            <span style={{ fontWeight: 700 }}>{ga.claimant_factor_pct?.toFixed(1)}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span>General Factor:</span>
                            <span style={{ fontWeight: 700 }}>{ga.general_factor_pct?.toFixed(1)}%</span>
                          </div>
                          <div className="pt-1 flex justify-between" style={{ borderTop: '1px solid var(--color-border)', color: 'var(--color-text-primary)', fontWeight: 700 }}>
                            <span>Incremental Lost Profit:</span>
                            <span><FinancialValue value={ga.incremental_lost_profit} /></span>
                          </div>
                        </div>
                      </div>

                      {/* Additional Expenses */}
                      {ae?.total_additional > 0 && (
                        <div className="rounded-lg p-4" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
                          <h4 className="mb-2" style={{ color: 'var(--color-text-primary)', fontWeight: 700 }}>Additional Expenses</h4>
                          <div className="cs-figure space-y-1">
                            <div className="flex justify-between">
                              <span>Accountant Fees:</span>
                              <span style={{ fontWeight: 700 }}><FinancialValue value={ae.accountant_fees} /></span>
                            </div>
                            <div className="flex justify-between">
                              <span>Other Adjustments:</span>
                              <span style={{ fontWeight: 700 }}><FinancialValue value={ae.other_adjustments} /></span>
                            </div>
                            <div className="pt-1 flex justify-between" style={{ borderTop: '1px solid var(--color-border)', color: 'var(--color-text-primary)', fontWeight: 700 }}>
                              <span>Total Additional:</span>
                              <span><FinancialValue value={ae.total_additional} /></span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Final Calculation */}
                      <div className="rounded-lg p-4" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-verified)' }}>
                        <h4 className="mb-2" style={{ color: 'var(--color-verified)', fontWeight: 700 }}>Final Calculation</h4>
                        <div className="cs-figure space-y-1">
                          <div className="flex justify-between">
                            <span>Step 1 (VP Loss):</span>
                            <span style={{ fontWeight: 700 }}><FinancialValue value={fc.step1_vp_loss} /></span>
                          </div>
                          <div className="flex justify-between">
                            <span>Step 2 (Incremental):</span>
                            <span style={{ fontWeight: 700 }}><FinancialValue value={fc.step2_incremental} /></span>
                          </div>
                          <div className="flex justify-between">
                            <span>Additional Expenses:</span>
                            <span style={{ fontWeight: 700 }}><FinancialValue value={fc.additional_expenses} /></span>
                          </div>
                          <div className="pt-2 mt-2 flex justify-between text-lg" style={{ borderTop: '2px solid var(--color-verified)', color: 'var(--color-verified)', fontWeight: 700 }}>
                            <span>TOTAL COMPENSABLE LOSS:</span>
                            <span><FinancialValue value={fc.final_claim} cents /></span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Payroll Reconciliation */}
            {calculationResults?.payroll_reconciliation && (
              <div id="payroll-section" className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'transparent' }}>
                    <DollarSign className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
                  </div>
                  <h3 className="cs-h3">Payroll Reconciliation</h3>
                </div>

                {(() => {
                  const recon = calculationResults.payroll_reconciliation;
                  const k1Detected = recon.k1_properly_separated || recon.flags?.includes('K1_PROPERLY_SEPARATED');

                  return (
                    <div className="p-4 rounded-lg" style={{ background: 'var(--color-bg-elevated)', border: `1px solid ${k1Detected ? 'var(--color-border)' : 'var(--color-verified)'}` }}>
                      <div className="flex items-center gap-2 mb-2">
                        {k1Detected ? <Info className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />}
                        <span style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>
                          {k1Detected ? 'K-1 Income Detected' : 'Employee Payroll Verified'}
                        </span>
                      </div>
                      <p className="cs-meta">
                        {k1Detected
                          ? 'Partner K-1 income has been properly separated from employee payroll for accurate VP calculations.'
                          : `Payroll variance: ${recon.variance_pct || 0}%`}
                      </p>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Sensitivity Analysis */}
            <div id="sensitivity-section" className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-needs-human-tint)' }}>
                    <BarChart3 className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                  </div>
                  <h3 className="cs-h3">Sensitivity Analysis</h3>
                </div>
                <button
                  onClick={() => setShowSensitivity(!showSensitivity)}
                  className="cs-meta flex items-center gap-1"
                  style={{ color: 'var(--color-needs-human-text)', fontWeight: 600 }}
                >
                  {showSensitivity ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  {showSensitivity ? 'Hide' : 'Show'}
                </button>
              </div>

              {showSensitivity && (
                <div className="space-y-4">
                  <p className="cs-meta">
                    Test how your claim amount changes when key parameters vary ±20%.
                  </p>

                  <div>
                    <label className="block cs-label mb-2">
                      Test Parameter:
                    </label>
                    <select
                      value={sensitivityParam}
                      onChange={(e) => setSensitivityParam(e.target.value)}
                      className="w-full px-4 py-2 rounded-lg focus:outline-none"
                      style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
                    >
                      <option value="payroll">Employee Payroll (±20%)</option>
                      <option value="revenue">Revenue (±20%)</option>
                      <option value="variable_expenses">Variable Expenses (±20%)</option>
                    </select>
                  </div>

                  <button
                    onClick={runSensitivityAnalysis}
                    disabled={sensitivityLoading}
                    className="w-full py-3 rounded-lg transition disabled:opacity-50 flex items-center justify-center gap-2"
                    style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 700 }}
                  >
                    {sensitivityLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Running Analysis...
                      </>
                    ) : (
                      'Run Sensitivity Analysis'
                    )}
                  </button>

                  {sensitivityResults && (
                    <div className="rounded-lg p-4 mt-4" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
                      <h4 className="mb-3" style={{ color: 'var(--color-text-primary)', fontWeight: 700 }}>Results:</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead style={{ background: 'var(--color-bg-hover)' }}>
                            <tr>
                              <th className="px-4 py-2 text-left cs-label">Parameter %</th>
                              <th className="px-4 py-2 text-right cs-label">Claim Amount</th>
                              <th className="px-4 py-2 text-right cs-label">Change</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sensitivityResults.results.map((row: any, idx: number) => (
                              <tr key={idx} style={{ borderTop: '1px solid var(--color-border-faint)', background: row.pct === 100 ? 'var(--color-needs-human-tint)' : 'transparent', fontWeight: row.pct === 100 ? 700 : 400 }}>
                                <td className="px-4 py-2">{row.pct}%{row.pct === 100 ? ' (Base)' : ''}</td>
                                <td className="px-4 py-2 text-right cs-figure"><FinancialValue value={row.claim} /></td>
                                <td className="px-4 py-2 text-right" style={{ color: row.change_pct > 0 ? 'var(--color-verified)' : row.change_pct < 0 ? 'var(--color-text-muted)' : 'var(--color-text-muted)' }}>
                                  {row.change_pct > 0 ? '+' : ''}{row.change_pct}%
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className="mt-4 p-3 rounded" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
                        <p className="cs-meta" style={{ color: 'var(--color-needs-human-text)' }}>
                          <strong>Range:</strong> <FinancialValue value={sensitivityResults.range.min} /> - <FinancialValue value={sensitivityResults.range.max} />
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Monte Carlo Analysis */}
            <div id="montecarlo-section" className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-needs-human-tint)' }}>
                    <TrendingUp className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                  </div>
                  <div>
                    <h3 className="cs-h3">Monte Carlo Analysis</h3>
                    <span className="cs-chip px-2 py-0.5 rounded" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>FORENSIC-GRADE</span>
                  </div>
                </div>
                <button
                  onClick={() => setShowMonteCarlo(!showMonteCarlo)}
                  className="cs-meta flex items-center gap-1"
                  style={{ color: 'var(--color-needs-human-text)', fontWeight: 600 }}
                >
                  {showMonteCarlo ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  {showMonteCarlo ? 'Hide' : 'Show'}
                </button>
              </div>

              {showMonteCarlo && (
                <div className="space-y-4">
                  <p className="cs-meta">
                    Run 1,000+ simulations with randomized revenue variations (±15% Gaussian noise)
                    to produce 95% confidence intervals.
                  </p>

                  <button
                    onClick={runMonteCarloAnalysis}
                    disabled={monteCarloLoading}
                    className="w-full py-3 rounded-lg transition disabled:opacity-50 flex items-center justify-center gap-2"
                    style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 700 }}
                  >
                    {monteCarloLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Running 1,000 Simulations...
                      </>
                    ) : (
                      'Run Monte Carlo Analysis'
                    )}
                  </button>

                  {monteCarloResults && (
                    <div className="rounded-lg p-6 mt-4" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-verified)' }}>
                      <h4 className="mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)', fontWeight: 700 }}>
                        <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
                        Monte Carlo Complete
                        <span className="cs-meta">
                          ({monteCarloResults.iterations.toLocaleString()} iterations)
                        </span>
                      </h4>

                      <div className="rounded-lg p-4 mb-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                        <p className="cs-meta mb-1">Base Claim (Deterministic):</p>
                        <p className="cs-figure-lg" style={{ fontSize: '1.5rem' }}><FinancialValue value={monteCarloResults.base_claim} cents /></p>
                      </div>

                      <div className="rounded-lg p-4 mb-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-verified)' }}>
                        <p className="cs-meta mb-2" style={{ color: 'var(--color-verified)', fontWeight: 700 }}>95% Confidence Interval:</p>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="cs-meta">Low (2.5th)</p>
                            <p className="cs-figure" style={{ fontSize: '1.125rem', color: 'var(--color-verified)', fontWeight: 700 }}><FinancialValue value={monteCarloResults.results.ci_95_low} /></p>
                          </div>
                          <div className="text-center">
                            <p className="cs-meta">Median</p>
                            <p className="cs-figure" style={{ fontSize: '1.125rem', color: 'var(--color-text-primary)', fontWeight: 700 }}><FinancialValue value={monteCarloResults.results.median} /></p>
                          </div>
                          <div className="text-right">
                            <p className="cs-meta">High (97.5th)</p>
                            <p className="cs-figure" style={{ fontSize: '1.125rem', color: 'var(--color-verified)', fontWeight: 700 }}><FinancialValue value={monteCarloResults.results.ci_95_high} /></p>
                          </div>
                        </div>
                      </div>

                      <div className="p-3" style={{ background: 'var(--color-bg-surface)', borderLeft: '4px solid var(--color-brand)' }}>
                        <p className="cs-meta" style={{ color: 'var(--color-text-secondary)' }}>
                          <strong>Interpretation:</strong> With 95% confidence, the true claim amount
                          lies between <strong><FinancialValue value={monteCarloResults.results.ci_95_low} /></strong> and{' '}
                          <strong><FinancialValue value={monteCarloResults.results.ci_95_high} /></strong>.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Data Entry Briefing */}
            <div className="rounded-xl p-4" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="cs-h3 flex items-center gap-2" style={{ color: 'var(--color-needs-human-text)' }}>
                    <FileSpreadsheet className="w-5 h-5" />
                    Data Entry Briefing
                  </h3>
                  <p className="cs-meta mt-1" style={{ color: 'var(--color-needs-human-text)' }}>
                    Quick 3-5 page handoff document with gaps, flags, and action items.
                  </p>
                </div>
                <button
                  onClick={downloadBriefing}
                  disabled={briefingLoading}
                  className="px-6 py-2 rounded-lg transition flex items-center gap-2"
                  style={
                    briefingLoading
                      ? { background: 'var(--color-bg-elevated)', color: 'var(--color-text-dim)', fontWeight: 600, cursor: 'not-allowed' }
                      : { background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 600 }
                  }
                >
                  {briefingLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Download Briefing
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Download Options */}
            <div id="downloads-section" className="space-y-4">
              <h3 className="cs-h3">Download Reports</h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Standard Report */}
                <div className="rounded-xl p-6 hover:shadow-lg transition" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-verified)' }}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="cs-h3">Standard Report</h3>
                    <span className="cs-chip px-3 py-1 rounded-full" style={{ background: 'var(--color-verified)', color: 'var(--color-on-brand)' }}>FREE</span>
                  </div>
                  <p className="cs-meta mb-4">
                    Complete BI calculation report (11 pages) with all formulas and methodology.
                  </p>
                  <ul className="cs-body mb-4 space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 mt-0.5" style={{ color: 'var(--color-verified)' }} />
                      <span>Complete BI breakdown</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 mt-0.5" style={{ color: 'var(--color-verified)' }} />
                      <span>Causation test results</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 mt-0.5" style={{ color: 'var(--color-verified)' }} />
                      <span>Financial data tables</span>
                    </li>
                  </ul>
                  <button
                    onClick={handleDownloadPdf}
                    className="w-full py-3 rounded-lg transition flex items-center justify-center gap-2"
                    style={{ background: 'var(--color-verified)', color: 'var(--color-on-brand)', fontWeight: 700 }}
                  >
                    <FileText className="w-5 h-5" />
                    Download (11 pages)
                  </button>
                </div>

                {/* AI Narrative */}
                <div className="rounded-xl p-6 hover:shadow-lg transition" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="cs-h3" style={{ color: 'var(--color-needs-human-text)' }}>AI Narrative</h3>
                    <span className="cs-chip px-3 py-1 rounded-full" style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}>$150</span>
                  </div>
                  <p className="cs-body mb-4">
                    14-page court-ready legal narrative with professional language.
                  </p>
                  <ul className="cs-body mb-4 space-y-2">
                    <li className="flex items-start gap-2">
                      <Sparkles className="w-4 h-4 mt-0.5" style={{ color: 'var(--color-brand)' }} />
                      <span>AI-powered legal writing</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Sparkles className="w-4 h-4 mt-0.5" style={{ color: 'var(--color-brand)' }} />
                      <span>Expert testimony format</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Sparkles className="w-4 h-4 mt-0.5" style={{ color: 'var(--color-brand)' }} />
                      <span>60 seconds vs. 4 hours</span>
                    </li>
                  </ul>
                  <button
                    onClick={() => setShowNarrator(true)}
                    className="w-full py-3 rounded-lg transition shadow-lg flex items-center justify-center gap-2"
                    style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 700 }}
                  >
                    <Sparkles className="w-5 h-5" />
                    Generate AI Report
                  </button>
                </div>

                {/* Complete Bundle */}
                <div className="rounded-xl p-6 hover:shadow-lg transition relative" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
                  <div className="absolute -top-3 -right-3 cs-chip px-3 py-1 rounded-full shadow-lg" style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}>
                    SAVE $50
                  </div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="cs-h3" style={{ color: 'var(--color-needs-human-text)' }}>Complete Bundle</h3>
                    <span className="cs-chip px-3 py-1 rounded-full" style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}>$200</span>
                  </div>
                  <p className="cs-body mb-4">
                    Everything: Standard + AI Narrative + Premium analytics (25+ pages).
                  </p>
                  <ul className="cs-body mb-4 space-y-2">
                    <li className="flex items-start gap-2">
                      <Package className="w-4 h-4 mt-0.5" style={{ color: 'var(--color-brand)' }} />
                      <span>Standard Standard BI Report</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Package className="w-4 h-4 mt-0.5" style={{ color: 'var(--color-brand)' }} />
                      <span>AI Legal Narrative</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Package className="w-4 h-4 mt-0.5" style={{ color: 'var(--color-brand)' }} />
                      <span>Sensitivity + Monte Carlo</span>
                    </li>
                  </ul>
                  <button
                    onClick={() => setValidationError('Bundle generation coming soon!')}
                    className="w-full py-3 rounded-lg transition shadow-lg flex items-center justify-center gap-2"
                    style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 700 }}
                  >
                    <Package className="w-5 h-5" />
                    Get Bundle (25 pages)
                  </button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 justify-center pt-4">
              <button
                onClick={() => goToStep?.(7)}
                className="py-3 px-8 rounded-lg shadow-lg transition-all flex items-center gap-2"
                style={{ background: 'var(--color-verified)', color: 'var(--color-on-brand)', fontWeight: 700 }}
              >
                Continue to Final Review
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              <button
                onClick={() => navigate('/')}
                className="py-3 px-6 rounded-lg transition-colors"
                style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)', fontWeight: 700 }}
              >
                Save & Exit
              </button>
            </div>
          </>
        )}

      </div>
    </WizardV2Layout>
  );
}
